import { useMutation, useQuery } from '@apollo/client';
import { type TOppositionBase } from '../../types/oppositionBase';
import { useBranch } from '../../hooks/useBranch';
import { GET_OPPOSITION_BASES_BY_BRANCH } from './queries';
import { CREATE_OPPOSITION_BASE, REMOVE_OPPOSITION_BASE, UPDATE_OPPOSITION_BASE } from './mutations';

interface GetOppositionBasesResponse {
  getOppositionBasesByBranch: TOppositionBase[]
}

interface CreateOppositionBaseParams {
  name: string
  description: string
  file: string
  filename: string
}

interface UpdateOppositionBaseParams {
  _id: string
  name: string
  description: string
  file?: string
  filename?: string
}

export const useGetOppositionBases = () => {
  const { branch } = useBranch();
  const { data, loading, refetch } = useQuery<GetOppositionBasesResponse>(GET_OPPOSITION_BASES_BY_BRANCH, {
    variables: { branch: branch?._id },
    skip: !branch?._id
  });

  return {
    oppositionBases: data?.getOppositionBasesByBranch,
    loading,
    refetch
  };
};

export const useCreateOppositionBase = () => {
  const { branch } = useBranch();
  const [action, { loading }] = useMutation(CREATE_OPPOSITION_BASE);

  const createOppositionBase = async ({ name, description, file, filename }: CreateOppositionBaseParams) => {
    await action({
      variables: {
        input: {
          name,
          description,
          file,
          filename,
          branch: branch?._id,
          active: true,
          executedBy: 'changeForUser'
        }
      }
    });
  };

  return { createOppositionBase, loading };
};

export const useUpdateOppositionBase = () => {
  const [action, { loading }] = useMutation(UPDATE_OPPOSITION_BASE);

  const updateOppositionBase = async ({ _id, name, description, file, filename }: UpdateOppositionBaseParams) => {
    await action({
      variables: {
        input: {
          _id,
          name,
          description,
          file,
          filename,
          active: true,
          executedBy: 'changeForUser'
        }
      }
    });
  };

  return { updateOppositionBase, loading };
};

export const useRemoveOppositionBase = () => {
  const [action, { loading }] = useMutation(REMOVE_OPPOSITION_BASE);

  const removeOppositionBase = async (_id: string) => {
    await action({
      variables: { input: _id }
    });
  };

  return { removeOppositionBase, loading };
};
